/**
 * Every date and time FOX renders goes through this module.
 *
 * Two problems it exists to solve:
 *
 * 1. The API sends UTC. Some endpoints send it with a trailing Z, and older
 *    ones send a bare "2026-08-20T14:03:00". JavaScript parses a bare ISO
 *    datetime as *browser-local*, so those used to render five or six hours
 *    off with nothing on screen admitting it. parseUtc() treats an unzoned
 *    string as UTC, which is what it always was.
 *
 * 2. Users are spread across Central and Eastern. Formatting goes through
 *    Intl.DateTimeFormat with an IANA zone, so daylight saving comes from the
 *    tz database instead of the hand-rolled nth-Sunday arithmetic this file
 *    replaced.
 *
 * Usage:
 *   const { formatDateTime, formatRelative } = useTz();
 *   formatDateTime(row.UpdatedAt)   // "Aug 20, 2026, 9:03 AM CDT"
 *   formatRelative(row.UpdatedAt)   // "12 minutes ago"
 */

import React from "react";

/** Zones FOX users actually sit in, offered before the full IANA list. */
export const COMMON_TIMEZONES = [
  { value: "America/Chicago", label: "Central — Chicago (NSA, PFC)" },
  { value: "America/New_York", label: "Eastern — New York (BGFC)" },
  { value: "America/Denver", label: "Mountain — Denver" },
  { value: "America/Phoenix", label: "Mountain, no DST — Phoenix" },
  { value: "America/Los_Angeles", label: "Pacific — Los Angeles" },
  { value: "America/Anchorage", label: "Alaska — Anchorage" },
  { value: "Pacific/Honolulu", label: "Hawaii — Honolulu" },
  { value: "UTC", label: "UTC — Zulu" },
];

/** Whatever zone this browser thinks it is in. */
export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
  } catch {
    return "America/Chicago";
  }
}

/** The full IANA list when the runtime exposes it, else the curated set. */
export function allTimezones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch {
    /* fall through */
  }
  return COMMON_TIMEZONES.map((z) => z.value);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parse an API timestamp into a Date, treating an unzoned value as UTC.
 *
 * Returns null for anything unparseable so callers can fall back to a dash
 * rather than rendering "Invalid Date".
 */
export function parseUtc(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const str = String(value).trim();
  if (!str) return null;

  // A date-only column has no time and must not be given one: attaching
  // midnight UTC turns "the 3rd" into "the 2nd" everywhere west of London.
  // parseDateOnly() handles these; here we anchor at noon UTC so the calendar
  // day survives formatting in any US zone.
  if (DATE_ONLY.test(str)) {
    const parsed = new Date(`${str}T12:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const normalized = str.includes("T") ? str : str.replace(" ", "T");
  const zoned = HAS_ZONE.test(normalized) ? normalized : `${normalized}Z`;
  const parsed = new Date(zoned);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** The zone abbreviation ("CDT", "EST") for a zone at a given instant. */
export function timezoneAbbrev(timeZone, at) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(at || new Date());
    const name = parts.find((p) => p.type === "timeZoneName");
    return name ? name.value : "";
  } catch {
    return "";
  }
}

function makeFormatter(timeZone, options) {
  try {
    return new Intl.DateTimeFormat("en-US", { ...options, timeZone });
  } catch {
    return new Intl.DateTimeFormat("en-US", options);
  }
}

/**
 * Build the formatting helpers for one zone.
 *
 * Exported separately from the hook so non-React code (chart axis callbacks,
 * CSV export builders) can format without a component around it.
 */
export function createFormatters(timeZone) {
  const zone = timeZone || browserTimezone();

  const dateTime = makeFormatter(zone, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
  const dateTimeZoned = makeFormatter(zone, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
  const dateOnly = makeFormatter(zone, {
    month: "short", day: "numeric", year: "numeric",
  });
  const shortDate = makeFormatter(zone, { month: "short", day: "numeric" });
  const timeOnly = makeFormatter(zone, { hour: "numeric", minute: "2-digit" });
  const timeZoned = makeFormatter(zone, {
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });

  const fmt = (formatter, value, fallback) => {
    const d = parseUtc(value);
    return d ? formatter.format(d) : fallback;
  };

  return {
    timeZone: zone,

    /** "Aug 20, 2026, 9:03 AM" — pass {zoned: true} to append "CDT". */
    formatDateTime: (value, { zoned = false, fallback = "--" } = {}) =>
      fmt(zoned ? dateTimeZoned : dateTime, value, fallback),

    /** "Aug 20, 2026" */
    formatDate: (value, { fallback = "--" } = {}) => fmt(dateOnly, value, fallback),

    /** "Aug 20" */
    formatShortDate: (value, { fallback = "--" } = {}) => fmt(shortDate, value, fallback),

    /** "9:03 AM" — pass {zoned: true} to append "CDT". */
    formatTime: (value, { zoned = false, fallback = "--" } = {}) =>
      fmt(zoned ? timeZoned : timeOnly, value, fallback),

    /**
     * "12 minutes ago", "3 hours ago", "yesterday", "Aug 12".
     * Anything older than six days falls back to an absolute date, which
     * reads better than "13 days ago" on a stale-data stamp.
     */
    formatRelative: (value, { now = null, fallback = "--" } = {}) => {
      const d = parseUtc(value);
      if (!d) return fallback;
      const ref = now instanceof Date ? now : new Date();
      const seconds = Math.round((ref.getTime() - d.getTime()) / 1000);

      if (seconds < 0) return "just now";
      if (seconds < 45) return "just now";
      if (seconds < 90) return "1 minute ago";

      const minutes = Math.round(seconds / 60);
      if (minutes < 60) return `${minutes} minutes ago`;

      const hours = Math.round(minutes / 60);
      if (hours === 1) return "1 hour ago";
      if (hours < 24) return `${hours} hours ago`;

      const days = Math.round(hours / 24);
      if (days === 1) return "yesterday";
      if (days <= 6) return `${days} days ago`;

      return dateOnly.format(d);
    },

    /** "CDT" for right now in this zone. */
    abbrev: (at) => timezoneAbbrev(zone, at),

    /** Calendar day in this zone as YYYY-MM-DD, for API date params. */
    toZonedDateKey: (value) => {
      const d = parseUtc(value);
      if (!d) return null;
      const parts = makeFormatter(zone, {
        year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(d);
      const get = (type) => (parts.find((p) => p.type === type) || {}).value;
      const y = get("year");
      const m = get("month");
      const day = get("day");
      return y && m && day ? `${y}-${m}-${day}` : null;
    },
  };
}

const TimezoneContext = React.createContext(null);

/**
 * Supplies the active zone to the tree.
 *
 * `preference` is the user's saved zone, or null/"" to follow the browser.
 */
export function TimezoneProvider({ preference, children }) {
  const value = React.useMemo(() => {
    const zone = preference || browserTimezone();
    return { ...createFormatters(zone), preference: preference || null };
  }, [preference]);

  return (
    <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>
  );
}

/**
 * Formatting helpers in the current user's zone.
 *
 * Safe to call outside a provider — it falls back to the browser's zone, so a
 * component rendered in a modal portal or a test never throws.
 */
export function useTz() {
  const ctx = React.useContext(TimezoneContext);
  const fallback = React.useMemo(() => createFormatters(browserTimezone()), []);
  return ctx || fallback;
}

export default TimezoneContext;
